import { useEffect, useRef, useState } from 'react';
import { Storage, adoptServerWrite } from '../lib/storage';
import { useServerApplied } from './serverApplied';
import type { SetToast } from './toast';

/** The two keys a tournament lives in. It moves between them; never both. */
export type TournamentKey = 'active_tournament_v1' | 'paused_tournaments_v1';

export interface UseTournamentsDeps {
  /** The store's toast slot — where a failed tournament write is reported. */
  setToast: SetToast;
}

/**
 * What a stored tournament key holds, as the in-memory slice would hold it —
 * or `undefined` when the stored text is not a document this slice can take.
 *
 * `undefined` is not «nothing stored». An unreadable row must never replace a
 * live tournament on screen with an empty one: that is how a document still in
 * the database appeared to vanish once before (#413).
 */
function parseStored(key: TournamentKey, value: string | null): any {
  if (key === 'active_tournament_v1') {
    // '' is how the persist effect stores «no active tournament».
    if (!value) return null;
    try {
      const doc = JSON.parse(value);
      return doc && typeof doc === 'object' ? doc : undefined;
    } catch {
      return undefined;
    }
  }
  if (value == null || value === '') return [];
  try {
    const list = JSON.parse(value);
    return Array.isArray(list) ? list : undefined;
  } catch {
    return undefined;
  }
}

// ─────────────────────────────────────────────────────────────────────
// useTournaments — the active + paused tournament slices, lifted out of the
// Padel24AppMain god component (see docs/history/DECOMPOSITION_PLAN.md §5 R1, PR-G).
// (extracted from the former monolith)
//
// THIS IS THE DANGEROUS LIFT. Both slices use the §13.20 R1 pattern: a
// BLANKET-PERSIST useEffect that writes Storage on EVERY state change, guarded by
// a useRef loaded-flag so it NEVER writes before the initial mount load has run.
// If state and flag were ever separated, the empty init-state would persist over
// real saved data on first render — SILENT DATA LOSS. So each slice is a TRIO
// that MUST stay together, and all three pieces live here:
//   (1) the useState slice,
//   (2) a useRef loaded-flag (starts false),
//   (3) a blanket-persist useEffect that bails early via `if (!flag.current) return;`.
//
//   • tournament         (useState null, key 'active_tournament_v1', TENANT-SCOPED)
//                        — the active/in-progress tournament (Padel24 or Americano).
//                        The persist effect writes the serialized tournament, or the
//                        empty string '' when it is null (the loader treats a falsy
//                        .value as absent — keep the '' write; do NOT switch to delete).
//   • pausedTournaments  (useState [], key 'paused_tournaments_v1')
//                        — parked tournaments that can be resumed. The persist effect
//                        always writes the serialized array.
//
// LOADER BRIDGE — the load lives in the app's ATOMIC mount loader (one big
// useEffect that reads ~13 domains in one pass — DECOMPOSITION_PLAN principle #1).
// We do NOT add a load effect here. The loader flips the refs AFTER it reads each
// value, with a DELIBERATE in/out-of-`cancelled` asymmetry that lives in the loader
// and is not normalized: `activeTournLoaded.current = true` flips OUTSIDE the
// cancelled guard while `pausedLoaded.current = true` flips INSIDE it. For those
// lines to resolve, this hook EXPOSES both refs.
//
// EXPOSE: each slice's state + its setter + its ref. The ~30 setTournament call
// sites and the setPausedTournaments sites live in the loader and use the exposed
// setters. NOTE: `drafts` (separate, NON-R1, no persist flag) lives there too — it
// is NOT owned here.
//
// Everything below (slice ordering, keys, the empty-string '' write for the null
// case, and the async-IIFE styling) is copied VERBATIM from the former monolith.
// Do not reorder or "fix" any of it. The ONE deliberate departure is the error
// handling: a failed write used to be logged and nothing else, and is now
// reported to the person with an offer to reload the stored version (#1305 —
// see `reportTournamentSaveFailure` below).
// ─────────────────────────────────────────────────────────────────────

export function useTournaments({ setToast }: UseTournamentsDeps) {
  // ──── SLICE A — active tournament ('active_tournament_v1', tenant-scoped) ────
  const [tournament, setTournament] = useState(null); // engine state for the active tournament (Padel24 or Americano)
  // Persists the ACTIVE/in-progress tournament to Storage (the database) so it
  // survives reload — on a par with paused tournaments and the active series.
  // Writes on every change, clears it when it is parked/archived/reset.
  const activeTournLoaded = useRef(false);
  const activeFromServer = useServerApplied<any>();
  useEffect(() => {
    if (!activeTournLoaded.current) return; // do not write before the initial load has run
    if (activeFromServer.consume(tournament)) return; // the server already stored this one
    (async () => {
      try {
        if (tournament) await Storage.set('active_tournament_v1', JSON.stringify(tournament));
        else await Storage.set('active_tournament_v1', '');
      } catch (e) {
        reportTournamentSaveFailure('active_tournament_v1', e);
      }
    })();
    // `reportTournamentSaveFailure` reads only refs and stable setters, so it is
    // deliberately not a dependency: a new closure each render must not
    // re-fire a write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament, activeFromServer]);

  // ──── SLICE B — paused tournaments ('paused_tournaments_v1') ────
  const [pausedTournaments, setPausedTournaments] = useState([]); // parked tournaments (can be resumed)
  // Blanket-persist: saves ALL parked/in-progress tournaments on every change,
  // regardless of which handler changed the list. Guarantees that e.g. club
  // americano does not disappear on reload even if a setPausedTournaments path
  // were to forget persistPaused. (The active tournament is saved separately via active_tournament_v1.)
  const pausedLoaded = useRef(false);
  const pausedFromServer = useServerApplied<any[]>();
  useEffect(() => {
    if (!pausedLoaded.current) return; // do not write before the initial load has run
    if (pausedFromServer.consume(pausedTournaments)) return; // the server already stored this one
    (async () => {
      try {
        await Storage.set('paused_tournaments_v1', JSON.stringify(pausedTournaments));
      } catch (e) {
        reportTournamentSaveFailure('paused_tournaments_v1', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pausedTournaments, pausedFromServer]);

  // ──── A TOURNAMENT WRITE THAT DID NOT LAND (#1305) ────
  //
  // Both persist effects above, and `persistPaused` in the lifecycle hook,
  // used to catch a failed write and only log it. The screen kept showing a
  // tournament the database did not have, and the next reload lost it.
  //
  // Every failure ends up here, and they are not told apart: a 409 (another
  // session moved the key), a write refused locally because an earlier one
  // left the copy stale, and an ambiguous 5xx or dropped connection all mean
  // the same thing to the person — «this was not saved» — and all leave the
  // key in must-refresh (lib/storage.ts), so no write to it goes out again
  // until it has been re-read.
  //
  // THE OFFER IS THE STORED VERSION. «Last inn på nytt» reads each failed key
  // and puts what is stored on screen. It never re-sends the in-memory copy:
  // that copy is exactly what was refused, and sending it again once the read
  // has refreshed the version would overwrite whatever made it stale. The
  // effects skip the reloaded value (`*FromServer.mark`) because it is already
  // stored. Not reloading is also allowed — dismissing keeps the screen as it
  // is, and the next write to the key is refused and reported again.
  const failedKeys = useRef(new Set<TournamentKey>());
  const reloadStoredTournaments = async () => {
    setToast(null);
    const keys = [...failedKeys.current];
    for (const key of keys) {
      // `read`, not `get`: `get` answers null for a failed request too, and a
      // failed reload must leave the screen alone rather than empty it.
      const res = await Storage.read(key);
      const stored =
        res.status === 'failed'
          ? undefined
          : parseStored(key, res.status === 'ok' ? res.value : null);
      if (stored === undefined) {
        reportTournamentSaveFailure(key, new Error(`reload of ${key} failed (${res.status})`));
        continue;
      }
      failedKeys.current.delete(key);
      if (key === 'active_tournament_v1') {
        activeFromServer.mark(stored);
        setTournament(stored);
      } else {
        pausedFromServer.mark(stored);
        setPausedTournaments(stored);
      }
    }
  };
  /**
   * Report a tournament write that did not land, and offer the stored version.
   * The error goes to the console for diagnosis; the person gets the toast.
   */
  function reportTournamentSaveFailure(key: TournamentKey, error: unknown) {
    console.error(`[Padel24] Turneringen ble ikke lagret (${key}):`, error);
    failedKeys.current.add(key);
    setToast({
      type: 'error',
      message: '',
      messageKey: 'tournament.saveFailed.message',
      action: {
        labelKey: 'tournament.saveFailed.reload',
        run: () => void reloadStoredTournaments(),
      },
    });
  }

  /**
   * Take a tournament the SERVER has already applied and stored.
   *
   * Storage-LAST rather than storage-first, and deliberately: the write has
   * already happened, server-side, inside the transaction that claimed the
   * match and moved the ratings. There is nothing left to persist — only a
   * version to adopt, so this browser's next ordinary write is not refused for
   * a change it asked for.
   *
   * `written` is the document the server stored, which for a paused
   * tournament is the whole LIST and not `next`. The caller passes what it is
   * about to hold; `adoptServerWrite` will not take a version without it.
   */
  const adoptServerTournament = (next: any, storage?: { key: string; version: number }) => {
    activeFromServer.mark(next);
    if (storage) adoptServerWrite(storage.key, JSON.stringify(next), storage.version);
    setTournament(next);
  };

  /**
   * The paused list, after the server wrote one of its entries.
   *
   * `holdsDocument` is false in the only caller there is, and says something
   * true: the row's value is the whole LIST, the command's response carried
   * one SESSION, and the list here was rebuilt by substitution. That is right
   * almost always — and «almost» is what a precondition exists to catch — so
   * the version is not adopted and the key is marked for a refresh instead.
   * The screen is still correct; the next write just costs one read.
   */
  const adoptServerPausedTournaments = (
    nextList: any[],
    storage?: { key: string; version: number },
    holdsDocument = true,
  ) => {
    pausedFromServer.mark(nextList);
    if (storage) {
      adoptServerWrite(
        storage.key,
        holdsDocument ? JSON.stringify(nextList) : null,
        storage.version,
      );
    }
    setPausedTournaments(nextList);
  };

  return {
    // SLICE A
    tournament,
    setTournament,
    adoptServerTournament,
    adoptServerPausedTournaments,
    // The R1 loaded-flag — owned here, flipped by the atomic mount loader
    // (OUTSIDE the `cancelled` guard) AFTER it reads 'active_tournament_v1',
    // so the persist effect never writes before load.
    activeTournLoaded,
    // SLICE B
    pausedTournaments,
    setPausedTournaments,
    // The R1 loaded-flag — flipped by the atomic mount loader (INSIDE the
    // `cancelled` guard) AFTER it reads 'paused_tournaments_v1'.
    pausedLoaded,
    // #1305 — for the lifecycle hook's own writer of the paused list.
    reportTournamentSaveFailure,
  };
}
